import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Canvas,
  Image as FabricImage,
  Textbox,
  Rect,
  Ellipse,
  Circle,
  Line,
  Triangle,
} from "fabric";
import {
  Container,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { REGEX } from "constants/CommonConstants";
import staticQr from "assets/img/qr_code/scalent.jpeg";

const certificateValues = {
  trainee_name: "Amit Narayan Parmar",
  trainer_name: "Raj Chauhan",
  course_name: "AWS",
  issue_date: "12 Nov 2024",
  certificate_no: "raycham123",
  completion_date: "12 Dec 2024",
  course_duration: "48 hrs",
  assessment_score: "76.8 %",
  verification_url: "https://in.linkedin.com/company/scalent",
  qr_code: staticQr,
};

const ViewCertificate = () => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [downloadOpen, setDownloadOpen] = useState(false);

  const certificateTemplate =
    location.state?.template ||
    JSON.parse(localStorage.getItem("currentTemplate"));

  useEffect(() => {
    if (!certificateTemplate) return;

    const { width, height } = certificateTemplate.canvasSize;

    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      selection: false,
    });

    fabricCanvasRef.current = canvas;

    const render = async () => {
      if (certificateTemplate.previewImage) {
        const bg = await FabricImage.fromURL(
          require("assets/img/certificates_templates/certificate3.jpg"),
        );
        bg.set({
          left: canvas.width / 2,
          top: canvas.height / 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        bg.scaleToWidth(canvas.width);
        bg.scaleToHeight(canvas.height);
        canvas.backgroundImage = bg;
      }

      for (const item of certificateTemplate.layout) {
        const left = (item.x / 100) * canvas.width;
        const top = (item.y / 100) * canvas.height;

        if (item.type === "text") {
          let value = item.text;

          value = replaceInlinePlaceholders(item.text, certificateValues);

          canvas.add(
            new Textbox(value, {
              left,
              top,
              width: item.width || undefined,
              fontSize: item.fontSize,
              fontFamily: item.fontFamily,
              fill: item.color,
              angle: item.angle || 0,
              textAlign: item.textAlign,
              originX: item.originX,
              originY: item.originY,
              scaleX: item.scaleX,
              scaleY: item.scaleY,

              selectable: false,
              evented: false,
            }),
          );
        }

        if (item.type === "qr") {
          const qrSrc = certificateValues.qr_code || item.src; // dynamic QR or static

          if (!qrSrc) return;

          const img = await FabricImage.fromURL(qrSrc);

          img.set({
            left,
            top,
            originX: "center",
            originY: "center",
            angle: item.angle || 0,
            selectable: false,
            evented: false,
          });

          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);

          canvas.add(img);
        }

        if (item.type === "image") {
          const img = await FabricImage.fromURL(item.src);
          img.set({
            left,
            top,
            originX: "center",
            originY: "center",
            selectable: false,
            evented: false,
          });
          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);
          canvas.add(img);
        }

        if (item.type === "signature") {
          const img = await FabricImage.fromURL(item.src);

          img.set({
            left,
            top,
            originX: "center",
            originY: "center",
            angle: item.angle || 0,
            scaleX: item.scaleX || 1,
            scaleY: item.scaleY || 1,
            selectable: false,
            evented: false,
          });

          img.scaleToWidth(item.width);
          img.scaleToHeight(item.height);

          canvas.add(img);
        }

        if (item.type === "shape") {
          let shape;

          switch (item.shapeType) {
            case "ellipse":
              shape = new Ellipse({
                rx: item.rx,
                ry: item.ry,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "circle":
              shape = new Circle({
                radius: item.radius,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "rect":
              shape = new Rect({
                width: item.width,
                height: item.height,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
                scaleX: item.scaleX || 1,
                scaleY: item.scaleY || 1,
              });

              break;

            case "triangle":
              shape = new Triangle({
                width: item.width,
                height: item.height,
                fill: item.fill,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
              });
              break;

            case "line": {
              shape = new Line([item.x1, item.y1, item.x2, item.y2], {
                left,
                top,
                originX: "center",
                originY: "center",
                angle: item.angle || 0,
                scaleX: item.scaleX || 1,
                scaleY: item.scaleY || 1,
                stroke: item.stroke,
                strokeWidth: item.strokeWidth,
                strokeDashArray: item.dash,
                selectable: false,
                evented: false,
              });

              shape.setCoords();
              canvas.add(shape);
              continue;
            }
          }

          if (shape) {
            shape.set({
              originX: "center",
              originY: "center",
              left,
              top,
              angle: item.angle || 0,
              scaleX: item.scaleX || 1,
              scaleY: item.scaleY || 1,
              selectable: false,
              evented: false,
            });

            shape.setCoords();
            canvas.add(shape);
          }
        }
      }

      canvas.renderAll();
    };

    render();

    return () => {
      canvas.dispose();

      const goingBackToDesigner = sessionStorage.getItem("fromDesignerToView");

      if (!goingBackToDesigner) {
        localStorage.removeItem("currentTemplate");
      }
    };
  }, [certificateTemplate]);

  const toggleDownload = () => setDownloadOpen((v) => !v);

  const replaceInlinePlaceholders = (text, values) => {
    if (!text) return "";

    return text.replace(REGEX.INLINE_PLACEHOLDER, (match, key) => {
      return values[key] ?? match;
    });
  };

  const downloadAsPNG = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL({
      format: "png",
      quality: 1,
      multiplier: 2, // higher resolution
    });

    const link = document.createElement("a");
    link.href = dataURL;
    link.download = `${certificateTemplate.name || "certificate"}.png`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAsJPG = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL({
      format: "jpeg",
      quality: 0.95,
      multiplier: 2,
    });

    const link = document.createElement("a");
    link.href = dataURL;
    link.download = `${certificateTemplate.name || "certificate"}.jpg`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!certificateTemplate) {
    return (
      <div className="centered-message">
        <h3>No certificate template found</h3>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <Container fluid>
      <Row className="h-100 justify-content-center align-items-center">
        <Col
          xs="12"
          md="10"
          lg="8"
          className="d-flex flex-column align-items-center"
        >
          <h3 className="mb-3 text-center">{certificateTemplate.name}</h3>

          {/* CERTIFICATE WRAPPER */}
          <div className="bg-white p-3 position-relative pt-5 certificate-wrapper">
            {/* DOWNLOAD DROPDOWN */}
            <div className="certificate-download-actions">
              <Dropdown isOpen={downloadOpen} toggle={toggleDownload}>
                <DropdownToggle caret color="primary" size="sm">
                  <i className="fa fa-download mr-1" />
                  Download
                </DropdownToggle>

                <DropdownMenu end>
                  <DropdownItem
                    onClick={() => {
                      setDownloadOpen(false);
                      downloadAsPNG();
                    }}
                  >
                    Download as PNG
                  </DropdownItem>

                  <DropdownItem
                    onClick={() => {
                      setDownloadOpen(false);
                      downloadAsJPG();
                    }}
                  >
                    Download as JPG
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>

            <div className="mb-3 w-100 d-flex justify-content-start align-items-center">
              <i
                className="fa fa-chevron-left mr-3 cursor"
                onClick={() => {
                  sessionStorage.setItem("fromDesignerToView", "true");
                  navigate("/admin/certificate/create-certificate", {
                    state: { fromView: true },
                  });
                }}
              />
              <span className="font-weight-600">Back to Designer</span>
            </div>

            {/* THIS IS MANDATORY */}
            <canvas ref={canvasRef} />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ViewCertificate;
